import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  ShoppingCartIcon,
  Squares2X2Icon,
  CubeIcon,
  ClockIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  CubeTransparentIcon,
  CheckIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { CREATE_DISCOUNT_RULE, ADD_DISCOUNT_CODE } from '../../graphql/discount-mutations'
import { GET_DISCOUNT_RULES } from '../../graphql/discount-queries'
import { GET_CATEGORIES, GET_PRODUCTS } from '../../graphql/product-queries'
import type {
  effect_type,
  scope_type,
  allocation_type,
  trigger_type,
  condition_type,
} from '../../types/discount'

// =============================================================================
// Types
// =============================================================================

interface ConditionValues {
  time_from: string
  time_to: string
  days: number[]
  min_order_value: string
  required_items: number[]
}

interface WizardState {
  name: string
  trigger_type: trigger_type
  min_staff_role: string
  scope_type: scope_type
  scope_value_ids: number[]
  effect_type: effect_type
  effect_value: string
  multi_buy_qty: string
  allocation: allocation_type
  conditionsEnabled: {
    time_window: boolean
    days_of_week: boolean
    min_order_value: boolean
    required_items: boolean
  }
  conditions: ConditionValues
  priority: string
  max_uses_total: string
  starts_at: string
  ends_at: string
}

const INITIAL: WizardState = {
  name: '',
  trigger_type: 'AUTOMATIC',
  min_staff_role: '',
  scope_type: 'ORDER',
  scope_value_ids: [],
  effect_type: 'PERCENTAGE',
  effect_value: '',
  multi_buy_qty: '2',
  allocation: 'EACH',
  conditionsEnabled: {
    time_window: false,
    days_of_week: false,
    min_order_value: false,
    required_items: false,
  },
  conditions: {
    time_from: '',
    time_to: '',
    days: [],
    min_order_value: '',
    required_items: [],
  },
  priority: '0',
  max_uses_total: '',
  starts_at: localDateTimeString(new Date()),
  ends_at: '',
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
function generateCode(): string {
  return Array.from({ length: 8 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join('')
}

function localDateTimeString(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}
const EXPIRY_DAYS = [7, 30, 60, 90]
const TOTAL_STEPS = 5
const STEP_TITLES = [
  'Give it a name',
  'What gets discounted?',
  'How much off?',
  'Add conditions',
  'Set limits',
]

// =============================================================================
// Live preview
// =============================================================================

function buildPreview(s: WizardState): string {
  if (!s.name) return ''
  if (!s.effect_value || parseFloat(s.effect_value) <= 0) return `"${s.name}" — still configuring…`

  const count = s.scope_value_ids.length
  const target =
    s.scope_type === 'ORDER'
      ? 'the whole order'
      : s.scope_type === 'CATEGORY'
        ? count > 0 ? `items in ${count} categor${count === 1 ? 'y' : 'ies'}` : 'items in selected categories'
        : count > 0 ? `${count} selected variant${count === 1 ? '' : 's'}` : 'selected variants'

  const trigger =
    s.trigger_type === 'AUTOMATIC'
      ? 'automatically'
      : s.trigger_type === 'CODE'
        ? 'when a coupon code is entered'
        : 'when applied by staff'

  const extras: string[] = []
  if (s.conditionsEnabled.time_window && s.conditions.time_from && s.conditions.time_to)
    extras.push(`between ${s.conditions.time_from}–${s.conditions.time_to}`)
  if (s.conditionsEnabled.days_of_week && s.conditions.days.length > 0)
    extras.push(`on ${s.conditions.days.map((d) => DAYS[d - 1]).join(', ')}`)
  if (s.conditionsEnabled.min_order_value && s.conditions.min_order_value)
    extras.push(`with a min. order of €${s.conditions.min_order_value}`)

  const suffix = extras.length > 0 ? `, ${extras.join(', ')}` : ''

  // Multi-buy reads as "any N for €X" rather than "X off".
  if (s.effect_type === 'MULTI_BUY') {
    const qty = parseInt(s.multi_buy_qty, 10)
    if (!Number.isInteger(qty) || qty < 2) return `"${s.name}" — still configuring…`
    return `"${s.name}" gives any ${qty} from ${target} for €${parseFloat(s.effect_value).toFixed(2)}, applied ${trigger}${suffix}.`
  }

  const amount =
    s.effect_type === 'PERCENTAGE' ? `${s.effect_value}%` : `€${parseFloat(s.effect_value).toFixed(2)}`

  return `"${s.name}" gives ${amount} off ${target}, applied ${trigger}${suffix}.`
}

// =============================================================================
// Shared option card
// =============================================================================

function OptionCard({
  selected,
  onClick,
  icon: Icon,
  title,
  description,
}: {
  selected: boolean
  onClick: () => void
  icon: React.ElementType
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border-2 flex items-start gap-4 transition-all ${
        selected
          ? 'border-indigo-600 bg-indigo-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div
        className={`mt-0.5 flex-shrink-0 rounded-lg p-2 ${
          selected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${selected ? 'text-indigo-900' : 'text-gray-900'}`}>{title}</div>
        <div className="text-xs text-gray-500 mt-0.5">{description}</div>
      </div>
      {selected && <CheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />}
    </button>
  )
}

// =============================================================================
// Step 1 — Name
// =============================================================================

function Step1({
  state,
  update,
}: {
  state: WizardState
  update: (p: Partial<WizardState>) => void
}) {
  return (
    <div className="space-y-3">
      <input
        autoFocus
        className="input w-full text-lg"
        value={state.name}
        onChange={(e) => update({ name: e.target.value })}
        placeholder="e.g. Happy hour, Loyalty reward, Staff discount"
      />
      <p className="text-xs text-gray-400">
        This is what your team sees when they search for it later.
      </p>
    </div>
  )
}


// =============================================================================
// Step 3 — Scope
// =============================================================================

function CategoryPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const { data, loading } = useQuery(GET_CATEGORIES)
  const categories: { id: number; name: string }[] = data?.categories ?? []

  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  )

  function toggle(id: number) {
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    )
  }

  return (
    <div className="space-y-2">
      <input
        className="input w-full text-sm"
        placeholder="Search categories…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
        {filtered.map((cat) => {
          const checked = selectedIds.includes(cat.id)
          return (
            <label
              key={cat.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(cat.id)}
                className="rounded"
              />
              <span className={`text-sm ${checked ? 'text-indigo-900 font-medium' : 'text-gray-700'}`}>
                {cat.name}
              </span>
            </label>
          )
        })}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 px-3 py-2">No categories found.</p>
        )}
      </div>
      {selectedIds.length > 0 && (
        <p className="text-xs text-indigo-600 font-medium">
          {selectedIds.length} categor{selectedIds.length === 1 ? 'y' : 'ies'} selected
        </p>
      )}
    </div>
  )
}

function VariantPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const { data, loading } = useQuery(GET_PRODUCTS)
  const products: {
    id: number
    name: string
    variants: { id: number; name: string; price_cents: number; active: boolean }[]
  }[] = data?.products ?? []

  const filtered = products
    .map((p) => ({
      ...p,
      variants: p.variants.filter(
        (v) =>
          v.active &&
          (p.name.toLowerCase().includes(search.toLowerCase()) ||
            v.name.toLowerCase().includes(search.toLowerCase()))
      ),
    }))
    .filter((p) => p.variants.length > 0)

  function toggle(id: number) {
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    )
  }

  function toggleAll(variantIds: number[]) {
    const allSelected = variantIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      onChange(selectedIds.filter((id) => !variantIds.includes(id)))
    } else {
      const toAdd = variantIds.filter((id) => !selectedIds.includes(id))
      onChange([...selectedIds, ...toAdd])
    }
  }

  return (
    <div className="space-y-2">
      <input
        className="input w-full text-sm"
        placeholder="Search products or variants…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
        {filtered.map((product) => {
          const variantIds = product.variants.map((v) => v.id)
          const allSelected = variantIds.every((id) => selectedIds.includes(id))
          const someSelected = variantIds.some((id) => selectedIds.includes(id))
          return (
            <div key={product.id} className="border border-gray-100 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleAll(variantIds)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  allSelected
                    ? 'bg-indigo-50 text-indigo-900'
                    : someSelected
                      ? 'bg-indigo-50/50 text-gray-900'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    allSelected
                      ? 'bg-indigo-600 border-indigo-600'
                      : someSelected
                        ? 'bg-indigo-200 border-indigo-400'
                        : 'border-gray-300'
                  }`}
                >
                  {(allSelected || someSelected) && (
                    <CheckIcon className="h-3 w-3 text-white" />
                  )}
                </span>
                {product.name}
              </button>
              <div className="divide-y divide-gray-50">
                {product.variants.map((v) => {
                  const checked = selectedIds.includes(v.id)
                  return (
                    <label
                      key={v.id}
                      className={`flex items-center gap-3 pl-8 pr-3 py-2 cursor-pointer transition-colors ${
                        checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(v.id)}
                        className="rounded"
                      />
                      <span className={`text-sm flex-1 ${checked ? 'text-indigo-900 font-medium' : 'text-gray-700'}`}>
                        {v.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        €{(v.price_cents / 100).toFixed(2)}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 px-3 py-2">No products found.</p>
        )}
      </div>
      {selectedIds.length > 0 && (
        <p className="text-xs text-indigo-600 font-medium">
          {selectedIds.length} variant{selectedIds.length === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  )
}

function Step3({
  state,
  update,
}: {
  state: WizardState
  update: (p: Partial<WizardState>) => void
}) {
  // A multi-buy groups qualifying products, so it can't target the whole order.
  const allowOrderScope = state.effect_type !== 'MULTI_BUY'

  return (
    <div className="space-y-3">
      {state.effect_type === 'MULTI_BUY' && (
        <p className="text-sm text-gray-500">
          A multi-buy deal applies to a set of products, so pick a category or specific items.
        </p>
      )}
      {allowOrderScope && (
        <OptionCard
          selected={state.scope_type === 'ORDER'}
          onClick={() => update({ scope_type: 'ORDER', scope_value_ids: [] })}
          icon={ShoppingCartIcon}
          title="The whole order"
          description="Reduces the total basket — e.g. 10% off everything"
        />
      )}
      <OptionCard
        selected={state.scope_type === 'CATEGORY'}
        onClick={() => update({ scope_type: 'CATEGORY', scope_value_ids: [] })}
        icon={Squares2X2Icon}
        title="A category"
        description="All items in one or more categories — e.g. all burgers, all wines"
      />
      {state.scope_type === 'CATEGORY' && (
        <div className="pl-4 border-l-2 border-indigo-200 pt-1">
          <CategoryPicker
            selectedIds={state.scope_value_ids}
            onChange={(ids) => update({ scope_value_ids: ids })}
          />
        </div>
      )}
      <OptionCard
        selected={state.scope_type === 'ITEM'}
        onClick={() => update({ scope_type: 'ITEM', scope_value_ids: [] })}
        icon={CubeIcon}
        title="Specific variants"
        description="Pick exact product variants — e.g. only the large latte"
      />
      {state.scope_type === 'ITEM' && (
        <div className="pl-4 border-l-2 border-indigo-200 pt-1">
          <VariantPicker
            selectedIds={state.scope_value_ids}
            onChange={(ids) => update({ scope_value_ids: ids })}
          />
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Step 4 — Amount
// =============================================================================

function Step4({
  state,
  update,
}: {
  state: WizardState
  update: (p: Partial<WizardState>) => void
}) {
  const showAllocation = state.scope_type !== 'ORDER' && state.effect_type === 'FIXED_AMOUNT'
  const isMultiBuy = state.effect_type === 'MULTI_BUY'

  function pickEffect(next: effect_type) {
    // MULTI_BUY can't be order-scoped; move off ORDER and clear any stale ids so
    // the user is prompted to pick a category/items back in the scope step.
    if (next === 'MULTI_BUY' && state.scope_type === 'ORDER') {
      update({ effect_type: next, scope_type: 'CATEGORY', scope_value_ids: [] })
    } else {
      update({ effect_type: next })
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <button
          type="button"
          onClick={() => pickEffect('PERCENTAGE')}
          className={`p-4 rounded-xl border-2 text-center transition-all ${
            state.effect_type === 'PERCENTAGE'
              ? 'border-indigo-600 bg-indigo-50 text-indigo-900'
              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="text-3xl font-bold mb-1">%</div>
          <div className="text-sm font-semibold">Percentage off</div>
          <div className="text-xs text-gray-500 mt-0.5">e.g. 15% off</div>
        </button>
        <button
          type="button"
          onClick={() => pickEffect('FIXED_AMOUNT')}
          className={`p-4 rounded-xl border-2 text-center transition-all ${
            state.effect_type === 'FIXED_AMOUNT'
              ? 'border-indigo-600 bg-indigo-50 text-indigo-900'
              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="text-3xl font-bold mb-1">€</div>
          <div className="text-sm font-semibold">Fixed amount off</div>
          <div className="text-xs text-gray-500 mt-0.5">e.g. €5 off</div>
        </button>
        <button
          type="button"
          onClick={() => pickEffect('MULTI_BUY')}
          className={`p-4 rounded-xl border-2 text-center transition-all ${
            isMultiBuy
              ? 'border-indigo-600 bg-indigo-50 text-indigo-900'
              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <TagIcon className="h-8 w-8 mx-auto mb-1" />
          <div className="text-sm font-semibold">Multi-buy</div>
          <div className="text-xs text-gray-500 mt-0.5">e.g. 2 for €12.50</div>
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {state.effect_type === 'PERCENTAGE'
            ? 'How many percent off?'
            : isMultiBuy
              ? "What's the group price?"
              : 'How many euro off?'}
        </label>
        <div className="relative w-40">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium select-none">
            {state.effect_type === 'PERCENTAGE' ? '%' : '€'}
          </span>
          <input
            autoFocus
            type="number"
            min="0"
            step="0.01"
            className="input w-full pl-8 text-lg"
            value={state.effect_value}
            onChange={(e) => update({ effect_value: e.target.value })}
            placeholder={state.effect_type === 'PERCENTAGE' ? '10' : isMultiBuy ? '12.50' : '5.00'}
          />
        </div>
      </div>

      {isMultiBuy && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">How many for that price?</label>
          <div className="relative w-40">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium select-none">
              ×
            </span>
            <input
              type="number"
              min="2"
              step="1"
              className="input w-full pl-8 text-lg"
              value={state.multi_buy_qty}
              onChange={(e) => update({ multi_buy_qty: e.target.value })}
              placeholder="2"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">Group size — at least 2 qualifying items.</p>
          {state.scope_value_ids.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Go back to "What gets discounted?" to pick the qualifying category or items.
            </p>
          )}
        </div>
      )}

      {showAllocation && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-amber-900">
            How should this fixed amount apply?
          </p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="allocation"
              value="EACH"
              checked={state.allocation === 'EACH'}
              onChange={() => update({ allocation: 'EACH' })}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium text-amber-900">Apply to each qualifying item</div>
              <div className="text-xs text-amber-700">e.g. €5 off every eligible product</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="allocation"
              value="ACROSS"
              checked={state.allocation === 'ACROSS'}
              onChange={() => update({ allocation: 'ACROSS' })}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium text-amber-900">Split across all qualifying items</div>
              <div className="text-xs text-amber-700">e.g. €5 total, shared between all eligible products</div>
            </div>
          </label>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Step 5 — Conditions
// =============================================================================

function Step5({
  state,
  update,
}: {
  state: WizardState
  update: (p: Partial<WizardState>) => void
}) {
  function toggleCondition(key: keyof WizardState['conditionsEnabled']) {
    update({
      conditionsEnabled: { ...state.conditionsEnabled, [key]: !state.conditionsEnabled[key] },
    })
  }

  function patchCondition(patch: Partial<ConditionValues>) {
    update({ conditions: { ...state.conditions, ...patch } })
  }

  function toggleDay(day: number) {
    const days = state.conditions.days.includes(day)
      ? state.conditions.days.filter((d) => d !== day)
      : [...state.conditions.days, day]
    patchCondition({ days })
  }

  const pills: {
    key: keyof WizardState['conditionsEnabled']
    label: string
    Icon: React.ElementType
  }[] = [
    { key: 'time_window', label: 'Time window', Icon: ClockIcon },
    { key: 'days_of_week', label: 'Days of week', Icon: CalendarDaysIcon },
    { key: 'min_order_value', label: 'Min. order value', Icon: BanknotesIcon },
    { key: 'required_items', label: 'Required items', Icon: CubeTransparentIcon },
  ]

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Toggle conditions on — leave all off to always apply.
      </p>

      <div className="flex flex-wrap gap-2">
        {pills.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => toggleCondition(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
              state.conditionsEnabled[key]
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {state.conditionsEnabled.time_window && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <ClockIcon className="h-4 w-4 text-indigo-500" />
            Time window
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="time"
                className="input w-full"
                value={state.conditions.time_from}
                onChange={(e) => patchCondition({ time_from: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Until</label>
              <input
                type="time"
                className="input w-full"
                value={state.conditions.time_to}
                onChange={(e) => patchCondition({ time_to: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {state.conditionsEnabled.days_of_week && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <CalendarDaysIcon className="h-4 w-4 text-indigo-500" />
            Days of week
          </div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(i + 1)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  state.conditions.days.includes(i + 1)
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {state.conditionsEnabled.min_order_value && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <BanknotesIcon className="h-4 w-4 text-indigo-500" />
            Minimum order value
          </div>
          <div className="relative w-40">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">
              €
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input w-full pl-7"
              value={state.conditions.min_order_value}
              onChange={(e) => patchCondition({ min_order_value: e.target.value })}
              placeholder="20.00"
            />
          </div>
        </div>
      )}

      {state.conditionsEnabled.required_items && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <CubeTransparentIcon className="h-4 w-4 text-indigo-500" />
            Required items
          </div>
          <VariantPicker
            selectedIds={state.conditions.required_items}
            onChange={(ids) => patchCondition({ required_items: ids })}
          />
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Step 6 — Limits
// =============================================================================

function Step6({
  state,
  update,
}: {
  state: WizardState
  update: (p: Partial<WizardState>) => void
}) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        All optional — most discounts don't need caps or expiry dates.
      </p>

      {state.trigger_type === 'STAFF_APPLIED' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum role required{' '}
            <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <select
            className="input"
            value={state.min_staff_role}
            onChange={(e) => update({ min_staff_role: e.target.value })}
          >
            <option value="">Any staff member</option>
            <option value="cashier">Cashier</option>
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Max total uses</label>
        <input
          type="number"
          min="1"
          className="input w-40"
          value={state.max_uses_total}
          onChange={(e) => update({ max_uses_total: e.target.value })}
          placeholder="Unlimited"
        />
        <p className="text-xs text-gray-400 mt-1">Leave blank for unlimited uses.</p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Active from</label>
          <input
            type="datetime-local"
            className="input w-full"
            value={state.starts_at}
            onChange={(e) => update({ starts_at: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Active until</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {[1, 7, 30, 90].map((days) => {
              const base = state.starts_at ? new Date(state.starts_at) : new Date()
              const target = new Date(base)
              target.setDate(target.getDate() + days)
              const value = localDateTimeString(target)
              return (
                <button
                  key={days}
                  type="button"
                  onClick={() => update({ ends_at: value })}
                  className={`px-3 py-1 rounded-lg text-sm border transition-all ${
                    state.ends_at === value
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  +{days}d
                </button>
              )
            })}
            {state.ends_at && (
              <button
                type="button"
                onClick={() => update({ ends_at: '' })}
                className="px-3 py-1 rounded-lg text-sm border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"
              >
                Clear
              </button>
            )}
          </div>
          <input
            type="datetime-local"
            className="input w-full"
            value={state.ends_at}
            onChange={(e) => update({ ends_at: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Priority{' '}
          <span className="font-normal text-gray-400">(advanced)</span>
        </label>
        <input
          type="number"
          className="input w-24"
          value={state.priority}
          onChange={(e) => update({ priority: e.target.value })}
        />
        <p className="text-xs text-gray-400 mt-1">
          Higher value wins when multiple discounts qualify for the same order.
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// Main wizard
// =============================================================================

const TYPE_PARAM_MAP: Record<string, trigger_type> = {
  automatic: 'AUTOMATIC',
  coupon: 'CODE',
  staff: 'STAFF_APPLIED',
}

const TRIGGER_LABELS: Record<trigger_type, { title: string; backHref: string; backLabel: string }> = {
  AUTOMATIC: { title: 'New Automatic Discount Rule', backHref: '/discounts/automatic', backLabel: 'Automatic Discounts' },
  CODE: { title: 'New Coupon Code Rule', backHref: '/discounts/coupon', backLabel: 'Coupon Codes' },
  STAFF_APPLIED: { title: 'New Staff Discount Rule', backHref: '/discounts/staff', backLabel: 'Staff Discounts' },
}

export default function DiscountWizard() {
  const navigate = useNavigate()
  const { type } = useParams<{ type: string }>()
  const initialTriggerType: trigger_type = TYPE_PARAM_MAP[type ?? ''] ?? 'AUTOMATIC'
  const [step, setStep] = useState(1)
  const [state, setState] = useState<WizardState>({ ...INITIAL, trigger_type: initialTriggerType })
  const [savedRuleId, setSavedRuleId] = useState<number | null>(null)
  const [newCode, setNewCode] = useState('')
  const [newCodeDescription, setNewCodeDescription] = useState('')
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('')
  const [newCodeExpiresAt, setNewCodeExpiresAt] = useState('')

  function update(partial: Partial<WizardState>) {
    setState((s) => ({ ...s, ...partial }))
  }

  const [createRule, { loading }] = useMutation(CREATE_DISCOUNT_RULE, {
    refetchQueries: [GET_DISCOUNT_RULES],
    onCompleted(data) {
      const rule = data.createDiscountRule
      if (state.trigger_type === 'CODE') {
        setSavedRuleId(rule.id)
      } else {
        toast.success('Discount created!')
        navigate(`/discounts/${rule.id}`)
      }
    },
    onError(err) {
      toast.error(err.message)
    },
  })

  const [addCode, { loading: addingCode }] = useMutation(ADD_DISCOUNT_CODE, {
    onCompleted() {
      toast.success('Discount created!')
      navigate(`/discounts/${savedRuleId}`)
    },
    onError(err) {
      toast.error(err.message)
    },
  })

  function canProceed(): boolean {
    switch (step) {
      case 1:
        return state.name.trim().length > 0
      case 2:
        return state.scope_type === 'ORDER' || state.scope_value_ids.length > 0
      case 3: {
        if (!(parseFloat(state.effect_value) > 0)) return false
        if (state.effect_type === 'MULTI_BUY') {
          const qty = parseInt(state.multi_buy_qty, 10)
          // Multi-buy needs a valid group size and a set of qualifying products.
          return Number.isInteger(qty) && qty >= 2 && state.scope_value_ids.length > 0
        }
        return true
      }
      default:
        return true
    }
  }

  function handleNext() {
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1)
    } else {
      submitRule()
    }
  }

  function submitRule() {
    const parsedEffectValue = Math.round(parseFloat(state.effect_value) * 100)
    if (isNaN(parsedEffectValue) || parsedEffectValue <= 0) {
      return toast.error('Please enter a valid discount amount')
    }

    // Mirror the API's MULTI_BUY validation (utils/discountValidation.ts).
    let parsedMultiBuyQty: number | null = null
    if (state.effect_type === 'MULTI_BUY') {
      if (state.scope_type !== 'CATEGORY' && state.scope_type !== 'ITEM') {
        return toast.error('A MULTI_BUY discount must be scoped to CATEGORY or ITEM')
      }
      parsedMultiBuyQty = parseInt(state.multi_buy_qty, 10)
      if (!Number.isInteger(parsedMultiBuyQty) || parsedMultiBuyQty < 2) {
        return toast.error('Group size must be a whole number of at least 2')
      }
    }

    const discountClass = state.scope_type === 'ORDER' ? 'ORDER' : 'PRODUCT'

    const input: Record<string, unknown> = {
      name: state.name.trim(),
      discount_class: discountClass,
      effect_type: state.effect_type,
      effect_value: parsedEffectValue,
      scope_type: state.scope_type,
      // The EACH/ACROSS choice only matters for a non-ORDER FIXED_AMOUNT
      // discount — that's the only case where the allocation picker is shown.
      // Everything else must be ACROSS: an order-wide fixed amount has to be
      // subtracted once, not per unit, or it zeroes out any multi-item basket.
      allocation:
        state.scope_type !== 'ORDER' && state.effect_type === 'FIXED_AMOUNT'
          ? state.allocation
          : 'ACROSS',
      trigger_type: state.trigger_type,
      priority: parseInt(state.priority, 10) || 0,
    }

    if (state.scope_type !== 'ORDER' && state.scope_value_ids.length > 0) {
      input.scope_value_json = state.scope_value_ids
    }

    // Only send multi_buy_qty for MULTI_BUY — the API rejects it on other types.
    if (parsedMultiBuyQty != null) {
      input.multi_buy_qty = parsedMultiBuyQty
    }

    if (state.trigger_type === 'STAFF_APPLIED' && state.min_staff_role) {
      input.min_staff_role = state.min_staff_role
    }

    const conditions: Array<{ type: condition_type; value_json: unknown }> = []

    if (state.conditionsEnabled.time_window) {
      if (state.conditions.time_from)
        conditions.push({ type: 'TIME_FROM', value_json: state.conditions.time_from })
      if (state.conditions.time_to)
        conditions.push({ type: 'TIME_TO', value_json: state.conditions.time_to })
    }
    if (state.conditionsEnabled.days_of_week && state.conditions.days.length > 0) {
      conditions.push({ type: 'DAYS_OF_WEEK', value_json: state.conditions.days })
    }
    if (state.conditionsEnabled.min_order_value && state.conditions.min_order_value) {
      conditions.push({
        type: 'MIN_ORDER_VALUE',
        value_json: Math.round(parseFloat(state.conditions.min_order_value) * 100),
      })
    }
    if (state.conditionsEnabled.required_items && state.conditions.required_items.length > 0) {
      conditions.push({
        type: 'REQUIRED_ITEMS',
        value_json: state.conditions.required_items,
      })
    }

    if (conditions.length > 0) input.conditions = conditions

    const limitObj: Record<string, unknown> = {}
    if (state.max_uses_total) limitObj.max_uses_total = parseInt(state.max_uses_total, 10)
    if (state.starts_at) limitObj.starts_at = new Date(state.starts_at).toISOString()
    if (state.ends_at) limitObj.ends_at = new Date(state.ends_at).toISOString()
    if (Object.keys(limitObj).length > 0) input.limit = limitObj

    createRule({ variables: { input } })
  }

  function handleAddCode(e: React.FormEvent) {
    e.preventDefault()
    if (!newCode.trim()) return toast.error('Please enter a code')
    const vars: Record<string, unknown> = {
      rule_id: savedRuleId,
      code: newCode.trim().toUpperCase(),
    }
    if (newCodeDescription.trim()) vars.description = newCodeDescription.trim()
    if (newCodeMaxUses) vars.max_uses = parseInt(newCodeMaxUses, 10)
    if (newCodeExpiresAt) vars.expires_at = new Date(newCodeExpiresAt).toISOString()
    addCode({ variables: vars })
  }

  const preview = buildPreview(state)

  // ---- Code step (shown after rule saved with CODE trigger) ----
  if (savedRuleId !== null) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Rule saved!</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Add a coupon code</h1>
          <p className="text-sm text-gray-500 mt-1">
            Add at least one code so customers or staff can activate this discount. You can add
            more codes from the rule's detail page later.
          </p>
        </div>
        <form onSubmit={handleAddCode} className="card p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <div className="flex gap-2">
              <input
                autoFocus
                className="input flex-1 uppercase font-mono text-lg tracking-widest"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder="e.g. CURRY3"
              />
              <button
                type="button"
                onClick={() => setNewCode(generateCode())}
                className="btn-secondary flex items-center gap-1 text-sm"
                title="Generate random code"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Generate
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              className="input w-full"
              value={newCodeDescription}
              onChange={(e) => setNewCodeDescription(e.target.value)}
              placeholder="e.g. Summer promo for influencers"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max uses{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              min="1"
              className="input w-40"
              value={newCodeMaxUses}
              onChange={(e) => setNewCodeMaxUses(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires at{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="flex gap-1.5 flex-wrap mb-1.5">
              {EXPIRY_DAYS.map((days) => {
                const base = new Date()
                base.setDate(base.getDate() + days)
                const value = localDateTimeString(base)
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setNewCodeExpiresAt(value)}
                    className={`px-2 py-1 rounded text-xs border transition-all ${
                      newCodeExpiresAt === value
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    +{days}d
                  </button>
                )
              })}
              {newCodeExpiresAt && (
                <button
                  type="button"
                  onClick={() => setNewCodeExpiresAt('')}
                  className="px-2 py-1 rounded text-xs border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200"
                >
                  Clear
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              className="input w-full"
              value={newCodeExpiresAt}
              onChange={(e) => setNewCodeExpiresAt(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-4">
            <button type="submit" className="btn-primary" disabled={addingCode}>
              {addingCode ? 'Saving…' : 'Add code & finish'}
            </button>
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700 underline"
              onClick={() => navigate(`/discounts/${savedRuleId}`)}
            >
              Skip — add codes later
            </button>
          </div>
        </form>
      </div>
    )
  }

  const triggerLabel = TRIGGER_LABELS[state.trigger_type]

  // ---- Wizard ----
  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to={triggerLabel.backHref} className="text-sm text-gray-500 hover:text-gray-700">
          ← {triggerLabel.backLabel}
        </Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-2xl font-bold text-gray-900">{triggerLabel.title}</h1>
      </div>

      {state.trigger_type === 'STAFF_APPLIED' && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Staff-applied discounts are not yet integrated into the POS checkout. Cashiers cannot apply this discount during a sale yet.
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-1">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
          <div key={s} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => s < step && setStep(s)}
              title={s < step ? `Back to step ${s}` : undefined}
              className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center transition-all ${
                s < step
                  ? 'bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700'
                  : s === step
                    ? 'bg-indigo-600 text-white ring-2 ring-indigo-200 cursor-default'
                    : 'bg-gray-100 text-gray-400 cursor-default'
              }`}
            >
              {s < step ? <CheckIcon className="h-3.5 w-3.5" /> : s}
            </button>
            {s < TOTAL_STEPS && (
              <div className={`h-0.5 w-5 ${s < step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
        <span className="ml-2 text-sm text-gray-500 whitespace-nowrap">
          {STEP_TITLES[step - 1]}
        </span>
      </div>

      {/* Step content */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{STEP_TITLES[step - 1]}</h2>
        {step === 1 && <Step1 state={state} update={update} />}
        {step === 2 && <Step3 state={state} update={update} />}
        {step === 3 && <Step4 state={state} update={update} />}
        {step === 4 && <Step5 state={state} update={update} />}
        {step === 5 && <Step6 state={state} update={update} />}
      </div>

      {/* Live preview */}
      {preview && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wider mb-1">
            Preview
          </p>
          <p className="text-sm text-indigo-900">{preview}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => s - 1)}
          disabled={step === 1}
          className="btn-secondary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed() || loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? 'Creating…'
            : step === TOTAL_STEPS
              ? state.trigger_type === 'CODE'
                ? 'Create & add code →'
                : 'Create discount'
              : 'Next →'}
        </button>
      </div>
    </div>
  )
}
